import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import useHorizontalCarousel from "../Compartido/useHorizontalCarousel";
import '../../../../../css/web/hotelcamprodon/galeria.css';

// Galeria de la portada: totes les fotos de tots els destins en una graella
// "bento" (mides variades, sense forats), amb filtre per destí i un visor
// immersiu (fons difuminat, fletxes, teclat, gest de lliscar).
const Galeria = ({ destinos, ruta }) => {

    const { t } = useTranslation();

    const fotos = useMemo(() => {
        const llista = [];
        (destinos || []).forEach((destino) => {
            (destino.imagenes || []).forEach((imagen) => {
                if (!imagen.imagen) return;
                llista.push({
                    id: destino.id + "-" + imagen.id,
                    destinoId: destino.id,
                    destino: destino.elnombre,
                    src: ruta + "/destinos/" + destino.id + "/" + imagen.imagen,
                });
            });
        });
        return llista;
    }, [destinos, ruta]);

    const destinosConFotos = useMemo(() => {
        const vistos = new Map();
        fotos.forEach((foto) => {
            if (!vistos.has(foto.destinoId)) vistos.set(foto.destinoId, foto.destino);
        });
        return [...vistos.entries()].map(([id, nombre]) => ({ id, nombre }));
    }, [fotos]);

    const [filtro, setFiltro] = useState("todos");
    const [abierta, setAbierta] = useState(null);
    const botonCerrarRef = useRef(null);
    const inicioToqueRef = useRef(null);

    // Amb pocs destins el filtre és una càpsula compacta i centrada (sense canvis).
    // Amb muchos destins (>8, els que ja no caben en una fila als amplis habituals)
    // deixem de fer-los saltar de línia i els convertim en una tira amb scroll horitzontal,
    // amb fletxes — el mateix llenguatge que el carrusel de "Nuestros destinos" de sobre.
    const numeroFiltros = destinosConFotos.length + 1; // +1 pel botó "Todos"
    const mostrarFletxesFiltro = numeroFiltros > 8;
    const { trackRef: filtrosTrackRef, navState: navFiltros, scrollByCard: scrollFiltros } = useHorizontalCarousel('.bhg__filtro', numeroFiltros);

    const visibles = filtro === "todos" ? fotos : fotos.filter((foto) => foto.destinoId === filtro);

    // Amb poques fotos es veuen totes en una graella "bento". Amb més de 10
    // (fàcil si hi ha molts destins, com passa amb els filtres de sobre) la
    // pàgina s'allargava sense parar — ara la graella es queda en 10 fixes i
    // l'última fa d'accés al visor immersiu, que sí que navega per totes.
    const LIMITE_GRID = 10;
    const hayOcultas = visibles.length > LIMITE_GRID;
    const visiblesGrid = hayOcultas ? visibles.slice(0, LIMITE_GRID) : visibles;

    const cerrar = () => setAbierta(null);
    const ir = (delta) => setAbierta((actual) => (actual === null ? null : (actual + delta + visibles.length) % visibles.length));

    useEffect(() => {
        if (abierta === null) return;

        const alTeclear = (e) => {
            if (e.key === "Escape") cerrar();
            if (e.key === "ArrowRight") ir(1);
            if (e.key === "ArrowLeft") ir(-1);
        };

        const overflowAnterior = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", alTeclear);
        botonCerrarRef.current?.focus();

        return () => {
            window.removeEventListener("keydown", alTeclear);
            document.body.style.overflow = overflowAnterior;
        };
    }, [abierta === null, visibles.length]);

    if (fotos.length === 0) return null;

    const actual = abierta !== null ? visibles[abierta] : null;

    return (
        <section className="bhg">
            <div className="bhg__cabecera">
                <span className="bhh-eyebrow">{t("Galería")}</span>
                <h2>{t("Descubre los destinos que te esperan")}</h2>
            </div>

            {destinosConFotos.length > 1 && (
                <div className={"bhg__filtros-wrap" + (mostrarFletxesFiltro ? " bhg__filtros-wrap--con-fletxes" : "")}>
                    {mostrarFletxesFiltro && (
                        <button
                            type="button"
                            className="bhg__filtros-nav bhg__filtros-nav--prev"
                            disabled={navFiltros.atStart}
                            onClick={() => scrollFiltros(-1)}
                            aria-label={t("Anterior")}
                        >
                            <i className="fa-solid fa-arrow-left"></i>
                        </button>
                    )}
                    <div className="bhg__filtros" role="tablist" ref={filtrosTrackRef}>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={filtro === "todos"}
                            className={"bhg__filtro" + (filtro === "todos" ? " bhg__filtro--activo" : "")}
                            onClick={() => setFiltro("todos")}
                        >
                            {t("Todos")}
                        </button>
                        {destinosConFotos.map((destino) => (
                            <button
                                type="button"
                                role="tab"
                                key={destino.id}
                                aria-selected={filtro === destino.id}
                                className={"bhg__filtro" + (filtro === destino.id ? " bhg__filtro--activo" : "")}
                                onClick={() => setFiltro(destino.id)}
                            >
                                {destino.nombre}
                            </button>
                        ))}
                    </div>
                    {mostrarFletxesFiltro && (
                        <button
                            type="button"
                            className="bhg__filtros-nav bhg__filtros-nav--next"
                            disabled={navFiltros.atEnd}
                            onClick={() => scrollFiltros(1)}
                            aria-label={t("Siguiente")}
                        >
                            <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    )}
                </div>
            )}

            <div className={"bhg__grid" + (visiblesGrid.length <= 3 ? " bhg__grid--pocas bhg__grid--n" + visiblesGrid.length : "")}>
                {visiblesGrid.map((foto, index) => {
                    const esUltimaAmbMes = hayOcultas && index === LIMITE_GRID - 1;
                    return (
                        <button
                            type="button"
                            className={"bhg__tile" + (esUltimaAmbMes ? " bhg__tile--mas" : "")}
                            key={foto.id}
                            onClick={() => setAbierta(index)}
                            aria-label={esUltimaAmbMes
                                ? t("Ver todas las fotos") + " (" + visibles.length + ")"
                                : t("Ver foto ampliada") + " — " + foto.destino}
                        >
                            <img src={foto.src} alt={foto.destino} loading="lazy" />
                            {esUltimaAmbMes ? (
                                <span className="bhg__mas">
                                    <span className="bhg__mas-numero">+{visibles.length - LIMITE_GRID}</span>
                                    <span className="bhg__mas-texto">{t("Ver todas")}</span>
                                </span>
                            ) : (
                                <span className="bhg__chip">
                                    <i className="fa-solid fa-location-dot"></i> {foto.destino}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {actual && (
                <div
                    className="bhg-visor"
                    role="dialog"
                    aria-modal="true"
                    aria-label={actual.destino}
                    onClick={cerrar}
                    onTouchStart={(e) => { inicioToqueRef.current = e.touches[0].clientX; }}
                    onTouchEnd={(e) => {
                        if (inicioToqueRef.current === null) return;
                        const delta = e.changedTouches[0].clientX - inicioToqueRef.current;
                        inicioToqueRef.current = null;
                        if (Math.abs(delta) > 50) ir(delta < 0 ? 1 : -1);
                    }}
                >
                    <button ref={botonCerrarRef} type="button" className="bhg-visor__cerrar" onClick={cerrar} aria-label={t("Cerrar")}>
                        <i className="fa-solid fa-xmark"></i>
                    </button>

                    {visibles.length > 1 && (
                        <button type="button" className="bhg-visor__nav bhg-visor__nav--prev" onClick={(e) => { e.stopPropagation(); ir(-1); }} aria-label={t("Anterior")}>
                            <i className="fa-solid fa-arrow-left"></i>
                        </button>
                    )}

                    <figure className="bhg-visor__figura" onClick={(e) => e.stopPropagation()}>
                        <img key={actual.id} src={actual.src} alt={actual.destino} />
                        <figcaption>
                            <span>{actual.destino}</span>
                            <span>{abierta + 1} / {visibles.length}</span>
                        </figcaption>
                    </figure>

                    {visibles.length > 1 && (
                        <button type="button" className="bhg-visor__nav bhg-visor__nav--next" onClick={(e) => { e.stopPropagation(); ir(1); }} aria-label={t("Siguiente")}>
                            <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    )}
                </div>
            )}
        </section>
    );
};

export default Galeria;
